import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, ButtonBase, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useCommonApi } from '../../api';
import { PATHS } from '../../paths';
import { parseDate } from '../../utils/parseDate';
import { DealerOnly, TransparentChip, dashboardCardSx } from '../common';
import { Accident } from '../types/accident';

const RECENT_LIMIT = 5;

interface RecentAccidentRowProps {
  accident: Accident;
  onClick: () => void;
}

const RecentAccidentRow = ({ accident, onClick }: RecentAccidentRowProps) => {
  const date = accident.date ? parseDate(accident.date) : null;
  const car = [accident.car?.brand, accident.car?.model, accident.car?.plateNumber].filter(Boolean).join(' ');
  const isIncomplete = !accident.status?.progress?.isCompleted;

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple={false}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr auto', md: '96px minmax(0, 1.2fr) minmax(0, 1.4fr) auto 24px' },
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
        width: 1,
        textAlign: 'left',
        paddingY: 1.5,
        paddingX: { xs: 2, md: 3 },
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:hover': { backgroundColor: 'rgba(34, 34, 52, 0.03)' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 }
      }}
    >
      <Typography color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {date ? format(date, 'dd.MM.yyyy') : 'N/A'}
      </Typography>
      <Typography fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {accident.contractNumber}
      </Typography>
      <Typography noWrap sx={{ display: { xs: 'none', md: 'block' } }}>
        {car}
      </Typography>
      <Stack direction="row" gap={1} justifyContent="flex-end">
        {isIncomplete && <TransparentChip color="error" size="small" label="Doplnit" />}
        {accident.status && (
          <Chip
            size="small"
            label={accident.status.label}
            variant="filled"
            sx={{ backgroundColor: accident.status.color }}
          />
        )}
      </Stack>
      <ChevronRightIcon sx={{ display: { xs: 'none', md: 'block' }, color: 'text.disabled' }} />
    </ButtonBase>
  );
};

const RecentAccidentsWidgetConnectedContent = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery(useCommonApi().getAccidents({ limit: RECENT_LIMIT, offset: 0 }));
  const accidents = data?.items ?? [];

  return (
    <Box sx={{ ...dashboardCardSx, height: 1, overflow: 'hidden' }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        padding={{ xs: 2, md: 3 }}
        paddingBottom={2}
      >
        <Stack>
          <Typography variant="h6" fontWeight={600}>
            Poslední nehody
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Naposledy založené případy
          </Typography>
        </Stack>
        <Button
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(PATHS.ACCIDENTS())}
          sx={{ flexShrink: 0 }}
        >
          Všechny nehody
        </Button>
      </Stack>
      {isLoading ? (
        <Stack gap={1} padding={{ xs: 2, md: 3 }} paddingTop={1}>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} variant="rounded" height={40} />
          ))}
        </Stack>
      ) : accidents.length === 0 ? (
        <Box padding={{ xs: 2, md: 3 }} paddingTop={1}>
          <Typography color="text.secondary">Zatím tu nejsou žádné nehody.</Typography>
        </Box>
      ) : (
        accidents.map((accident) => (
          <RecentAccidentRow
            key={accident.id}
            accident={accident}
            onClick={() => navigate(PATHS.ACCIDENTS(accident.id))}
          />
        ))
      )}
    </Box>
  );
};

export const RecentAccidentsWidgetConnected = () => (
  <DealerOnly>
    <RecentAccidentsWidgetConnectedContent />
  </DealerOnly>
);
